import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { login, getCompanySettings } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { LogIn } from 'lucide-react';

const Login = ({ setUser }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);

  useEffect(() => {
    fetchCompanyInfo();
  }, []);

  const fetchCompanyInfo = async () => {
    try {
      const response = await getCompanySettings();
      setCompanyInfo(response.data);
    } catch (error) {
      // Company info not set, use defaults
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await login(formData);
      const { access_token, user } = response.data;
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      toast.success('Login successful!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4"
      style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1759724369762-376e0a29478b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMGJsdWUlMjB3aGl0ZSUyMGJhY2tncm91bmQlMjBtaW5pbWFsaXN0fGVufDB8fHx8MTc2NjA1NDI1NXww&ixlib=rb-4.1.0&q=85)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          {companyInfo?.logo_url && (
            <div className="flex justify-center mb-4">
              <img 
                src={companyInfo.logo_url} 
                alt="Company Logo" 
                className="h-20 w-20 object-contain"
                onError={(e) => e.target.style.display = 'none'}
              />
            </div>
          )}
          <h1 className="text-4xl font-bold text-indigo-600 mb-2" data-testid="login-page-title">
            {companyInfo?.company_name || 'T&N Business Services'}
          </h1>
          <p className="text-slate-600">Enterprise Human Resource Management System</p>
        </div>

        <Card className="shadow-xl border-slate-200 bg-white" data-testid="login-card">
          <CardHeader className="space-y-1 bg-white">
            <CardTitle className="text-2xl" data-testid="login-card-title">Sign In</CardTitle>
            <CardDescription data-testid="login-card-description">
              Enter your credentials to access your account
            </CardDescription>
          </CardHeader>
          <CardContent className="bg-white">
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
              <div className="space-y-2">
                <Label htmlFor="email" data-testid="email-label">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@nexushr.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="bg-slate-50 border-slate-200 focus:bg-white focus:border-indigo-500"
                  data-testid="email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" data-testid="password-label">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  className="bg-slate-50 border-slate-200 focus:bg-white focus:border-indigo-500"
                  data-testid="password-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-full py-6 font-medium transition-all shadow-sm hover:shadow-indigo-200"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? 'Signing in...' : (
                  <>
                    <LogIn className="mr-2" size={20} />
                    Sign In
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;